/**
 * Tarjeta compacta de evento para Director de Club.
 * Muestra un resumen rápido del evento y el estado de participación del club.
 * La información completa y acciones están en EventDetailPage.
 */

import { useMemo } from "react";
import { useNow } from "@/hooks/useNow";
import { Link } from "react-router-dom";
import { Card, Badge, Button } from "@/components/ui";
import {
  Calendar,
  MapPin,
  Users,
  Trophy,
  DollarSign,
  Clock,
  Lock,
  Building2,
  ChevronRight,
  UserPlus,
  Upload,
} from "lucide-react";
import { useClubEventPayments, useClubEventRanking } from "@/hooks/useEvents";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import type { Event, EventRegistration } from "@/types";

const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

const MIN_FOR_RANKING = 10;

const levelLabels: Record<string, string> = {
  association: "Asociación",
  union: "Unión",
  district: "Distrito",
  club: "Club",
};

interface ClubEventCardProps {
  event: Event;
  clubId: string;
  clubName: string;
  clubRegistrations: EventRegistration[];
  onRegisterMembers: (event: Event) => void;
  onUploadPayment: (event: Event) => void;
}

export default function ClubEventCard({
  event,
  clubId,
  clubRegistrations,
  onRegisterMembers,
  onUploadPayment,
}: ClubEventCardProps) {
  const { data: payments } = useClubEventPayments(clubId, event.id);
  const { data: ranking } = useClubEventRanking(clubId, event.id);

  // Tickea para que la tarjeta se abra sola al llegar la hora: el lunes del
  // campamento los directores dejan esta pantalla abierta esperando las 7:30,
  // y con `new Date()` calculado una sola vez al montar el botón "Inscribir"
  // no aparecía hasta recargar.
  //
  // Mientras la apertura siga en el futuro tickea cada segundo: con 15 s, dos
  // directores que cargaron la página con segundos de diferencia veían el
  // botón con hasta 15 segundos de desfase entre ellos, y el premio es a los
  // 10 primeros. Una vez abierto (o cerrado) basta medio minuto.
  const abreEnElFuturo =
    toSafeDate(event.registrationConfig?.startDate).getTime() > Date.now();
  const nowMs = useNow(abreEnElFuturo ? 1_000 : 30_000);

  const regWindow = useMemo(() => {
    const now = new Date(nowMs);
    const regStart = toSafeDate(event.registrationConfig?.startDate);
    const regEnd = toSafeDate(event.registrationConfig?.endDate);
    if (now < regStart) {
      const diffMs = regStart.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return {
        status: "not_open" as const,
        label: diffDays > 1 ? `Abre en ${diffDays}d` : "Abre pronto",
        canRegister: false,
      };
    }
    if (now > regEnd) {
      return {
        status: "closed" as const,
        label: "Cerrado",
        canRegister: false,
      };
    }
    const diffMs = regEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return {
      status: "open" as const,
      label: diffDays > 1 ? `Cierra en ${diffDays}d` : "Cierra pronto",
      canRegister: true,
    };
  }, [event, nowMs]);

  const activeRegistrations = useMemo(
    () =>
      clubRegistrations.filter((r) =>
        ["pending", "approved", "waitlist"].includes(r.status),
      ),
    [clubRegistrations],
  );

  const registeredCount = activeRegistrations.length;
  const progress = Math.min((registeredCount / MIN_FOR_RANKING) * 100, 100);
  const approvedAmount =
    payments
      ?.filter((p) => p.status === "approved")
      .reduce((sum, p) => sum + p.amount, 0) ?? 0;
  const totalOwed = activeRegistrations.reduce(
    (sum, r) => sum + (r.unitPrice || 0),
    0,
  );
  const pendingPayments =
    payments?.filter((p) => p.status === "pending").length ?? 0;
  const isRanked = !!ranking?.completedAt;
  const rankPosition = ranking?.position;

  const formatDateRange = (start: unknown, end: unknown) => {
    const startDate = toSafeDate(start);
    const endDate = toSafeDate(end);
    if (startDate.toDateString() === endDate.toDateString()) {
      return startDate.toLocaleDateString("es-CO", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    }
    return `${startDate.toLocaleDateString("es-CO", { day: "numeric", month: "short" })} – ${endDate.toLocaleDateString("es-CO", { day: "numeric", month: "short" })}`;
  };

  const recommendation = useMemo(() => {
    if (registeredCount === 0 && regWindow.canRegister)
      return {
        text: "Inscribe miembros para participar",
        color: "text-purple-600",
      };
    if (registeredCount > 0 && registeredCount < MIN_FOR_RANKING)
      return {
        text: `Faltan ${MIN_FOR_RANKING - registeredCount} inscritos para ranking`,
        color: "text-amber-600",
      };
    if (registeredCount >= MIN_FOR_RANKING && !payments?.length)
      return {
        text: "Sube un pago para entrar al ranking",
        color: "text-amber-600",
      };
    if (isRanked)
      return {
        text: `¡Clasificado #${rankPosition}! 🎉`,
        color: "text-emerald-600",
      };
    return null;
  }, [registeredCount, regWindow, payments, isRanked, rankPosition]);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-0 shadow-md">
      {/* ========== MOBILE LAYOUT (< sm) ========== */}
      <div className="sm:hidden">
        {/* Cover image como banner */}
        <Link to={`/events/${event.id}`} className="block relative">
          {event.coverImageUrl ? (
            <img
              src={event.coverImageUrl}
              alt={event.name}
              className="w-full h-32 object-cover"
            />
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Calendar className="w-12 h-12 text-white/40" />
            </div>
          )}
          {/* Overlay con badges */}
          <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
            <Badge
              className={`${getEventStatusColor(event.status)} text-[10px] shadow-sm`}
            >
              {getEventStatusLabel(event.status)}
            </Badge>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold shadow-sm ${
                regWindow.status === "open"
                  ? "bg-green-100 text-green-700"
                  : regWindow.status === "not_open"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-red-50 text-red-600"
              }`}
            >
              {regWindow.status === "open" ? (
                <Clock className="w-3 h-3" />
              ) : (
                <Lock className="w-3 h-3" />
              )}
              {regWindow.label}
            </span>
          </div>
          {/* Ranking badge si está clasificado */}
          {isRanked && rankPosition != null && (
            <div className="absolute bottom-2 right-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
              <Trophy className="w-3.5 h-3.5" />#{rankPosition}
            </div>
          )}
        </Link>

        <div className="p-3.5 space-y-3">
          {/* Título y meta */}
          <div>
            <Link
              to={`/events/${event.id}`}
              className="block text-base font-bold text-gray-900 leading-tight hover:text-purple-700 transition-colors"
            >
              {event.name}
            </Link>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-1.5">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDateRange(event.startDate, event.endDate)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.location.city}
              </span>
            </div>
          </div>

          {/* Stats grid 2 columnas */}
          <div className="grid grid-cols-2 gap-2">
            {/* Inscritos */}
            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                <Users className="w-3 h-3" />
                Inscritos
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-lg font-bold ${
                    registeredCount >= MIN_FOR_RANKING
                      ? "text-emerald-600"
                      : "text-gray-900"
                  }`}
                >
                  {registeredCount}
                </span>
                <span className="text-xs text-gray-400">
                  / {MIN_FOR_RANKING}
                </span>
              </div>
              <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden mt-1.5">
                <div
                  className={`h-full rounded-full transition-all ${
                    registeredCount >= MIN_FOR_RANKING
                      ? "bg-emerald-500"
                      : "bg-amber-400"
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Pagos */}
            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                <DollarSign className="w-3 h-3" />
                Pagado
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-lg font-bold ${
                    approvedAmount > 0 ? "text-emerald-600" : "text-gray-900"
                  }`}
                >
                  {approvedAmount > 0
                    ? `$${(approvedAmount / 1000).toFixed(0)}K`
                    : "$0"}
                </span>
                {totalOwed > 0 && (
                  <span className="text-[10px] text-gray-400">
                    / ${(totalOwed / 1000).toFixed(0)}K
                  </span>
                )}
              </div>
              {pendingPayments > 0 && (
                <p className="text-[10px] text-amber-600 mt-1 font-medium">
                  {pendingPayments} por aprobar
                </p>
              )}
            </div>
          </div>

          {/* Recomendación */}
          {recommendation && (
            <div
              className={`text-[11px] font-medium px-2 py-1.5 rounded-md bg-gray-50 ${recommendation.color}`}
            >
              💡 {recommendation.text}
            </div>
          )}

          {/* Acciones */}
          <div className="flex items-center gap-2">
            {regWindow.canRegister && (
              <Button
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  onRegisterMembers(event);
                }}
                size="sm"
                className="flex-1 text-xs h-9 shadow-sm"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Inscribir
              </Button>
            )}
            {activeRegistrations.length > 0 && (
              <Button
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  onUploadPayment(event);
                }}
                size="sm"
                variant="secondary"
                className="flex-1 text-xs h-9 text-amber-700 border-amber-300 hover:bg-amber-50"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Pago
              </Button>
            )}
            <Link
              to={`/events/${event.id}`}
              className="inline-flex items-center justify-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 px-3 h-9 rounded-lg hover:bg-purple-50 transition-colors border border-purple-200"
            >
              Ver
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* ========== DESKTOP LAYOUT (>= sm) ========== */}
      <div className="hidden sm:flex">
        {/* Cover image */}
        {event.coverImageUrl ? (
          <div className="w-40 flex-shrink-0 relative">
            <img
              src={event.coverImageUrl}
              alt={event.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-40 flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Calendar className="w-10 h-10 text-white/30" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Event info */}
          <div className="p-4 pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Link
                    to={`/events/${event.id}`}
                    className="text-base font-bold text-gray-900 truncate hover:text-purple-700 transition-colors"
                  >
                    {event.name}
                  </Link>
                  <Badge
                    className={`${getEventStatusColor(event.status)} text-[10px]`}
                  >
                    {getEventStatusLabel(event.status)}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDateRange(event.startDate, event.endDate)}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {event.location.city}
                  </span>
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {levelLabels[event.level] || event.level}
                  </span>
                </div>
              </div>
              {/* Registration window */}
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold flex-shrink-0 ${
                  regWindow.status === "open"
                    ? "bg-green-100 text-green-700"
                    : regWindow.status === "not_open"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-red-50 text-red-600"
                }`}
              >
                {regWindow.status === "open" ? (
                  <Clock className="w-3 h-3" />
                ) : (
                  <Lock className="w-3 h-3" />
                )}
                {regWindow.label}
              </span>
            </div>
          </div>

          {/* Bottom: stats + actions */}
          <div className="px-4 pb-3 flex items-center justify-between gap-3">
            {/* Quick stats */}
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <Users
                  className={`w-3.5 h-3.5 ${registeredCount >= MIN_FOR_RANKING ? "text-emerald-500" : "text-purple-500"}`}
                />
                <span className="font-bold text-gray-900">
                  {registeredCount}
                </span>
                <span className="text-gray-400">/{MIN_FOR_RANKING}</span>
                <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${registeredCount >= MIN_FOR_RANKING ? "bg-emerald-500" : "bg-amber-400"}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <DollarSign
                  className={`w-3.5 h-3.5 ${approvedAmount > 0 ? "text-emerald-500" : "text-gray-400"}`}
                />
                <span className="font-bold text-gray-900">
                  {approvedAmount > 0
                    ? `$${(approvedAmount / 1000).toFixed(0)}K`
                    : "$0"}
                </span>
                {totalOwed > 0 && (
                  <span className="text-gray-400 text-[10px]">
                    /{`$${(totalOwed / 1000).toFixed(0)}K`}
                  </span>
                )}
                {pendingPayments > 0 && (
                  <span
                    className="text-amber-600 text-[10px]"
                    title={`${pendingPayments} recibo(s) esperando aprobación de la asociación`}
                  >
                    ({pendingPayments} recibo{pendingPayments !== 1 ? "s" : ""}{" "}
                    por aprobar)
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <Trophy
                  className={`w-3.5 h-3.5 ${isRanked ? "text-amber-500" : "text-gray-400"}`}
                />
                <span className="font-bold text-gray-900">
                  {isRanked
                    ? rankPosition != null
                      ? `#${rankPosition}`
                      : "✓"
                    : "—"}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {regWindow.canRegister && (
                <Button
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    onRegisterMembers(event);
                  }}
                  size="sm"
                  className="text-xs px-2.5 py-1.5 h-auto shadow-sm"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1" />
                  Inscribir
                </Button>
              )}
              {activeRegistrations.length > 0 && (
                <Button
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    onUploadPayment(event);
                  }}
                  size="sm"
                  variant="secondary"
                  className="text-xs px-2.5 py-1.5 h-auto text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <Upload className="w-3.5 h-3.5 mr-1" />
                  Pago
                </Button>
              )}
              <Link
                to={`/events/${event.id}`}
                className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 px-2 py-1.5 rounded-lg hover:bg-purple-50 transition-colors"
              >
                Ver
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Recommendation line */}
          {recommendation && (
            <div className="px-4 pb-2.5">
              <p className={`text-[11px] font-medium ${recommendation.color}`}>
                💡 {recommendation.text}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
